import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { IFacilityProject, IPage, IProject } from 'common/src/types/interfaces';

import { NEW_ID } from 'portal/src/constants/ReservedIDs';
import { numberFormatter } from 'portal/src/utils/numberFormatter';

import { deleteProject, getProjects } from '../../api/ProjectsService';
import DataTable from '../common/DataTable';
import addDateColumnProps from '../common/tables/addDateColumnProps';

const Projects = () => {
  const navigate = useNavigate();

  const {
    data: projects,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => await getProjects(),
    initialData: { data: [] } as IPage<IProject>,
  });

  const gridColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    {
      field: 'startDate',
      ...addDateColumnProps('Start Date', 140),
    },
    {
      field: 'completionDate',
      ...addDateColumnProps('Completion Date', 140),
    },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 140 },
    {
      field: 'facilities',
      headerName: 'Facilities',
      flex: 1,
      minWidth: 120,
      valueFormatter: (value: IFacilityProject[]) => {
        const facilityNames = value?.map((pg) => pg.facility.name);
        return facilityNames.join(', ');
      },
    },
    {
      field: 'estimatedCost',
      headerName: 'Estimated Cost',
      width: 190,
      renderCell: ({ value }) => numberFormatter(value),
    },
    {
      field: 'estimatedRebate',
      headerName: 'Estimated Rebate',
      width: 190,
      renderCell: ({ value }) => numberFormatter(value),
    },
    {
      field: 'estimatedUtilitySavings',
      headerName: 'Estimated Utility Savings',
      width: 190,
      renderCell: ({ value }) => numberFormatter(value),
    },
  ];

  const handleNavigate = (id: string) => navigate(`./${id}`);

  return (
    <DataTable
      actionsConfig={{
        readActionConfig: {
          onClick: (id) => handleNavigate(id),
        },
        deleteConfig: {
          targetStr: 'project',
          onDeleteClick: async (id) => {
            await deleteProject(id);
            await refetch();
          },
          snackbarMsgKey: 'PROJECT',
        },
      }}
      columns={gridColumns}
      contextName={NESTED_ROUTES.ADMINISTRATION.PROJECTS}
      dataFetchError={isError}
      loading={isFetching}
      rows={projects.data}
      toolbarSlotProps={{
        exportFileName: 'projects',
        onNewButtonClick: () => handleNavigate(NEW_ID),
        showFilters: true,
      }}
    />
  );
};

export default Projects;
